/**
 * The wire protocol between every portal and the realtime gateway.
 *
 * Shared by server and client so a rename cannot land on one side only — a
 * mistyped socket event name is not a compile error anywhere, it is a feature
 * that quietly stops working.
 */

/** Client → server. */
export const RealtimeClientEvents = {
  /** Start receiving typing/read signals for a thread the user just opened. */
  CONVERSATION_JOIN: 'conversation.join',
  /** Stop; the user navigated away. */
  CONVERSATION_LEAVE: 'conversation.leave',
  TYPING_START: 'typing.start',
  TYPING_STOP: 'typing.stop',
  /**
   * A supervisor's live team map asking to receive one municipality's officer
   * positions. The gateway checks `tracking:view` there before joining.
   */
  TRACKING_SUBSCRIBE: 'tracking.subscribe',
  TRACKING_UNSUBSCRIBE: 'tracking.unsubscribe',
  /**
   * A supervisor's safety board asking to receive one municipality's timers
   * and alerts. The gateway checks `safety:respond` there before joining.
   */
  SAFETY_SUBSCRIBE: 'safety.subscribe',
  SAFETY_UNSUBSCRIBE: 'safety.unsubscribe',
} as const;

/** Server → client. */
export const RealtimeServerEvents = {
  /** A new message in a thread this user belongs to. */
  MESSAGE_NEW: 'message.new',
  MESSAGE_UPDATED: 'message.updated',
  MESSAGE_DELETED: 'message.deleted',
  /** A conversation's summary changed — new preview, unread count, membership. */
  CONVERSATION_UPDATED: 'conversation.updated',
  /** Someone is typing in a thread the receiver currently has open. */
  TYPING: 'typing',
  /** A member read up to a point in the thread. */
  READ: 'read',
  /** A new in-app notification for the bell, pushed rather than polled. */
  NOTIFICATION_NEW: 'notification.new',
  /** The recipient's unread notification count changed. */
  NOTIFICATION_COUNT: 'notification.count',
  /**
   * A visit on a file changed status. Sent to the file's officers only — the
   * assigned officer, the file team and the visiting officer — never to a
   * citizen, whose sockets authenticate against a different API altogether.
   */
  VISIT_UPDATED: 'visit.updated',
  /**
   * An on-duty officer's latest position. Only ever emitted to
   * {@link trackingRoom}, which only sockets holding `tracking:view` at that
   * municipality can join.
   */
  OFFICER_POSITION: 'officer.position',
  /** An officer went on or off duty — the live map adds or drops their marker. */
  OFFICER_DUTY: 'officer.duty',
  /**
   * An officer safety event (SOS or missed check-in) was raised, escalated,
   * acknowledged or resolved. Sent to each supervisor alerted (their user
   * room), to {@link safetyRoom}, and to the officer it concerns.
   */
  SAFETY_ALERT: 'safety.alert',
  /** A check-in timer started, checked in, went overdue or closed. */
  SAFETY_TIMER: 'safety.timer',
} as const;

export type RealtimeClientEvent = (typeof RealtimeClientEvents)[keyof typeof RealtimeClientEvents];
export type RealtimeServerEvent = (typeof RealtimeServerEvents)[keyof typeof RealtimeServerEvents];

export interface TypingPayload {
  conversationId: string;
  userId: string;
  displayName: string;
  /**
   * `true` on start, `false` on stop — both arrive as the same `typing` event.
   *
   * One event carrying a flag rather than two events, so a client that missed
   * the start cannot be left showing an indicator forever: every message it
   * receives tells it the current state outright.
   */
  typing: boolean;
}

export interface ReadPayload {
  conversationId: string;
  userId: string;
  /** ISO timestamp the reader has now seen up to. */
  readAt: string;
}

export interface SafetySubscribePayload {
  localGovernmentId: string;
}

export interface TrackingSubscribePayload {
  localGovernmentId: string;
}

export interface ConversationJoinPayload {
  conversationId: string;
}
